import { COLUMN_OFFSET, MIN_COLUMN_WIDTH, ROW_OFFSET, TIME_COLUMN_WIDTH, CELL_HEIGHT } from '$features/calendar/constants'
import { useHeight, useWidth } from '$hooks'
import { useCalendar } from '$features/calendar/hooks/use-calendar'
import { useCalendarSlots } from '$features/calendar/hooks/use-calendar-slots'
import { useAppointmentPosition } from '$features/calendar/hooks/use-appointment-position'
import { useEffectiveSlotHeight } from '$features/calendar/hooks/use-effective-slot-height'
import { AnimatedOutlet } from '$components'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { CalendarFilterParam } from '$features/calendar/enums/filter-params'
import { useMemo, memo, Fragment } from 'react'
import { appointmentCalendarOptions } from '$api-client/@tanstack/react-query.gen'
import { Appointments } from '../appointments'
import { CalendarHeader } from '../calendar-header'
import { TimeSlots } from '../time-slots'
import { GridSlots } from '../grid-slots'

export const DayView = memo(function DayView() {
  const { date } = useCalendar()
  const days = [date]
  const [searchParams] = useSearchParams()

  const [containerHeight, outerRef] = useHeight<HTMLDivElement>()
  const [containerWidth, gridRef] = useWidth<HTMLDivElement>()

  const { data: appointments, isLoading: isLoadingAppointments } = useQuery({
    ...appointmentCalendarOptions({
      query: {
        start_at: date.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        finish_at: date.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        groomers: searchParams.getAll(CalendarFilterParam.PERSON_ID),
      },
    }),
  })

  const people = useMemo(() =>
    appointments?.data.map(group => ({
      id: group.id,
      first_name: group.first_name,
      last_name: group.last_name,
      schedule: group.schedule,
    })) ?? []
  , [appointments?.data])

  const { getSlots } = useCalendarSlots({ weekDays: days, people })

  const timeSlots = useMemo(() => getSlots(0), [getSlots])
  const startingTimeIndex = useMemo(() => timeSlots.findIndex(s => s.finish.minute() === 0) - 1, [timeSlots])

  const slotHeight = useEffectiveSlotHeight(containerHeight, timeSlots.length)

  const appointmentPositions = useAppointmentPosition({
    data: appointments?.data ?? [],
    gridWidth: containerWidth,
    slotHeight,
    getSlots,
    weekDays: days,
  })

  const columnCount = people.length === 0 ? 1 : people.length

  const slotGrids = useMemo(() => {
    const dayIndex = 0

    if (people.length > 0) {
      return people.map((person, groomerKey) => {
        const slots = getSlots(dayIndex, person.id)
        return slots.map((slot, key) => ({
          slot,
          key: `${dayIndex}-${groomerKey}-${key}`,
          dayIndex,
          groomerKey,
          slotKey: key,
          gridColumnStart: groomerKey + COLUMN_OFFSET,
          gridRowStart: key + ROW_OFFSET,
        }))
      }).flat()
    } else {
      const slots = getSlots(dayIndex)
      return slots.map((slot, key) => ({
        slot,
        key: `${dayIndex}-${key}`,
        dayIndex,
        groomerKey: 0,
        slotKey: key,
        gridColumnStart: COLUMN_OFFSET,
        gridRowStart: key + ROW_OFFSET,
      }))
    }
  }, [people, getSlots])

  return (
    <Fragment>
      <div
        ref={outerRef}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorX: 'contain',
          overscrollBehaviorY: 'contain',
        }}
        className="overflow-auto relative h-full"
      >
        <div
          ref={gridRef}
          style={{
            gridTemplateColumns: `${TIME_COLUMN_WIDTH}px repeat(${columnCount}, minmax(${MIN_COLUMN_WIDTH}px,1fr))`,
            gridTemplateRows: `48px repeat(${timeSlots.length}, minmax(${CELL_HEIGHT}px, 1fr))`,
            minWidth: 'max-content',
            height: '100%',
          }}
          className="grid relative"
        >
          {/* Corner Cell */}
          <div className="sticky top-0 left-0 z-40 bg-bg-subtle border-r border-b border-border h-12 col-start-1 row-start-1" />

          {/* Calendar Headers */}
          <CalendarHeader
            days={days}
            people={people}
            isLoading={isLoadingAppointments}
          />

          {/* Time Slots Column */}
          <TimeSlots
            timeSlots={timeSlots}
            startingTimeIndex={startingTimeIndex}
          />

          {/* Grid Slots */}
          <GridSlots
            slotGrids={slotGrids}
            days={days}
            people={people}
          />

          {/* Appointments */}
          <Appointments appointmentPositions={appointmentPositions} />
        </div>
      </div>

      <AnimatedOutlet />
    </Fragment>
  )})
